import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../../../../components/common/button";
import { Input, PhoneInput } from "../../../../../../components/common/input";
import { Text } from "../../../../../../components/common/typography";
import { SegmentedControl } from "../../../../../../components/common/segmented-control";
import { AppRoutes } from "../../../../../../common/routes/app_routes";
import { handleAxiosError } from "../../../../../../common/error_handling";
import { authOtpService } from "../../../../../api_client";
import {
  MIN_PASSWORD_LENGTH,
  validateEmail,
  validateFullName,
} from "../../../../../../utils/validation";
import {
  useRegisterWizard,
  type SignupMethod,
} from "../../../../domain/register-wizard";
import RegisterWizardLayout from "./register-wizard-layout";
import { DevAutofillButton } from "./dev-autofill-button";

const SIGNUP_TABS = [
  { value: "email" as const, label: "Email" },
  { value: "phone" as const, label: "Phone" },
];

function methodFromParams(params: URLSearchParams): SignupMethod {
  return params.get("method") === "phone" ? "phone" : "email";
}

export function CredentialsStep() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    email,
    phoneNumber,
    fullName,
    password,
    startSignup,
  } = useRegisterWizard();

  const signupMethod = methodFromParams(searchParams);

  const [localEmail, setLocalEmail] = useState(email);
  const [localPhone, setLocalPhone] = useState(phoneNumber);
  const [localName, setLocalName] = useState(fullName);
  const passwordRef = useRef<HTMLInputElement>(null!);
  const confirmRef = useRef<HTMLInputElement>(null!);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isPhone = signupMethod === "phone";

  const handleMethodChange = (method: SignupMethod) => {
    setSearchParams(
      method === "phone" ? { method: "phone" } : {},
      { replace: true },
    );
    setFieldError(null);
    setNameError(null);
    setPasswordError(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    setPasswordError(null);

    const fullName = localName.trim();
    const nameProblem = validateFullName(fullName);
    setNameError(nameProblem);

    if (isPhone) {
      if (!localPhone) {
        setFieldError("Please enter your phone number.");
        return;
      }
      if (nameProblem) return;
      setSubmitting(true);
      try {
        const res = await authOtpService.otpCheckAvailability({
          phoneNumber: localPhone,
        });
        if (res.data.phoneNumberAvailable === false) {
          setFieldError(
            "This phone number is already registered. Try signing in instead.",
          );
          return;
        }
        startSignup({ signupMethod: "phone", phoneNumber: localPhone, fullName });
        navigate(AppRoutes.CREATE_ACCOUNT_PARTNER_TYPE);
      } catch (err) {
        setFieldError(handleAxiosError(err).message);
      } finally {
        setSubmitting(false);
      }
    } else {
      const email = localEmail.trim();
      const emailProblem = validateEmail(email);
      setFieldError(emailProblem);
      const pwd = passwordRef.current.value;
      const confirm = confirmRef.current.value;
      if (pwd !== confirm) {
        setPasswordError("Passwords do not match.");
      } else if (pwd.length < MIN_PASSWORD_LENGTH) {
        setPasswordError(
          `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
        );
      }
      if (
        emailProblem ||
        nameProblem ||
        pwd !== confirm ||
        pwd.length < MIN_PASSWORD_LENGTH
      ) {
        return;
      }
      setSubmitting(true);
      try {
        const res = await authOtpService.otpCheckAvailability({ email });
        if (res.data.emailAvailable === false) {
          setFieldError(
            "This email is already registered. Try signing in instead.",
          );
          return;
        }
        startSignup({ signupMethod: "email", email, fullName, password: pwd });
        navigate(AppRoutes.CREATE_ACCOUNT_PARTNER_TYPE);
      } catch (err) {
        setFieldError(handleAxiosError(err).message);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const canSubmit = isPhone
    ? !!localPhone && !!localName && !submitting
    : !!localEmail && !!localName && !submitting;

  const autofill = () => {
    const suffix = Date.now().toString().slice(-6);
    if (isPhone) {
      setLocalPhone(`+974${suffix}99`);
      setLocalName("Dev Tester");
    } else {
      setLocalEmail(`dev+${suffix}@procurtal.test`);
      setLocalName("Dev Tester");
      if (passwordRef.current) passwordRef.current.value = "Password123!";
      if (confirmRef.current) confirmRef.current.value = "Password123!";
    }
    setFieldError(null);
    setNameError(null);
    setPasswordError(null);
  };

  return (
    <RegisterWizardLayout title="Sign up">
      <SegmentedControl
        aria-label="Sign up method"
        options={SIGNUP_TABS}
        value={signupMethod}
        onChange={handleMethodChange}
      />
      {/* noValidate: our inline messages replace the browser's validation bubbles. */}
      <form onSubmit={submit} noValidate className="flex w-full flex-col gap-6">
        {isPhone ? (
          <PhoneInput
            label="Phone number"
            required
            value={localPhone}
            onChange={(val) => {
              setLocalPhone(val);
              if (fieldError) setFieldError(null);
            }}
            error={fieldError ?? undefined}
          />
        ) : (
          <Input
            label="Email address"
            type="email"
            placeholder="email@example.com"
            autoComplete="email"
            value={localEmail}
            onChange={(e) => {
              setLocalEmail(e.target.value);
              if (fieldError) setFieldError(null);
            }}
            error={fieldError ?? undefined}
            required
          />
        )}
        <Input
          label="Full name"
          type="text"
          placeholder="Your first and last name"
          autoComplete="name"
          value={localName}
          onChange={(e) => {
            setLocalName(e.target.value);
            if (nameError) setNameError(null);
          }}
          error={nameError ?? undefined}
          required
        />
        {!isPhone && (
          <>
            <Input
              label="Password"
              type="password"
              placeholder="Enter your password"
              autoComplete="new-password"
              required
              ref={passwordRef}
              defaultValue={password}
            />
            <Input
              label="Re-enter password"
              type="password"
              placeholder="Re-enter your password"
              autoComplete="new-password"
              required
              ref={confirmRef}
              error={passwordError ?? undefined}
              defaultValue={password}
            />
          </>
        )}
        <Button variant="primary" type="submit" fullWidth disabled={!canSubmit}>
          {submitting ? "Checking…" : "Continue"}
        </Button>
        <DevAutofillButton onClick={autofill} />

        <div className="flex w-full items-center justify-center">
          <Text variant="body" tone="muted" inline>
            Already have an account?{" "}
            <button
              type="button"
              onClick={() =>
                navigate(
                  isPhone
                    ? `${AppRoutes.LOGIN}?method=phone`
                    : AppRoutes.LOGIN,
                )
              }
              className="bg-transparent"
            >
              <Text variant="bodyBold" tone="dark" inline>
                Sign in
              </Text>
            </button>
          </Text>
        </div>
      </form>
    </RegisterWizardLayout>
  );
}

export default CredentialsStep;
